/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import G17Client from '../../../services/G17Client';
import { addSiteAlert, SiteAlertColors } from '../../../slice/siteAlertsSlice';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { AuthenticatedRequestToJoinData, BaseStepCompProps, OnboardingMatches, RequestToJoinData } from '../types';
import { RequestSent } from './RequestSent';
import { useAppDispatch } from '@reducers/index';

const initialState: AuthenticatedRequestToJoinData = {
  initiativeIds: [],
  message: '',
};

interface RequestToJoinFormProps extends Pick<BaseStepCompProps, 'appConfig' | 'formData' | 'handleNext'> {
  initiatives: OnboardingMatches[];
}

export const RequestToJoinForm = (props: RequestToJoinFormProps) => {
  const { initiatives, appConfig, handleNext } = props;

  const dispatch = useAppDispatch();

  const [requestForm, setRequestForm] = useState<AuthenticatedRequestToJoinData>({
    ...initialState,
    initiativeIds: initiatives.length === 1 ? [initiatives[0]._id] : [],
  });
  const [isLoading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const isFormValid = requestForm.initiativeIds.length > 0;

  const handleChange = (field: keyof RequestToJoinData, value: string) => {
    setRequestForm((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const toggleCompany = (initiativeId: string) => {
    setRequestForm((prevData) => {
      if (prevData.initiativeIds.includes(initiativeId)) {
        return {
          ...prevData,
          initiativeIds: prevData.initiativeIds.filter((id) => id !== initiativeId),
        };
      }
      return {
        ...prevData,
        initiativeIds: [...new Set(prevData.initiativeIds).add(initiativeId).values()],
      };
    });
  };

  const handleSendRequest = () => {
    if (!isFormValid || !initiatives || !appConfig.onboardingPath) {
      return;
    }
    setLoading(true);
    G17Client.sendAuthenticatedRequestToJoin(requestForm, appConfig.onboardingPath)
      .then(() => {
        setLoading(false);
        setRequestSent(true);
      })
      .catch((e) => {
        setLoading(false);
        dispatch(
          addSiteAlert({
            content: e.message ?? 'Could not send request to join. Please try again',
            color: SiteAlertColors.Danger,
          }),
        );
      });
  };

  if (initiatives.length === 0) {
    return (
      <div className='d-lg-flex justify-content-between onboarding-form'>
        <aside className='title-container'>
          <div className='h1 mb-3'>Error</div>
          <p>Something went wrong, please refresh the page and try again.</p>
        </aside>
      </div>
    );
  }

  if (requestSent) {
    return (
      <RequestSent
        appConfig={appConfig}
        requests={initiatives.filter((initiative) => requestForm.initiativeIds.includes(initiative._id))}
      />
    );
  }

  return (
    <div className='d-lg-flex justify-content-between onboarding-form'>
      {isLoading ? <BlockingLoader /> : null}
      <aside className='title-container'>
        <div className='h4 text-strong mb-3'>We think your company might already have an account...</div>
        <p>
          <u>
            <a href='mailto:support@g17.eco' className='text-ThemeHeadingLight'>
              Contact us if you think this is a mistake
            </a>
          </u>
        </p>
      </aside>
      <section className='form-container'>
        <div className='mb-2'>
          {initiatives.length > 1 ? (
            <>
              Is one of these your company?
              <br />
              Select which one you want to join
            </>
          ) : (
            'Is this your company?'
          )}
        </div>
        <div className='mt-3'>
          {initiatives.map((initiative) => (
            <div key={`initiative-${initiative._id}`} className='text-ThemeAccentDark text-lg my-2 ml-5'>
              <Input
                type='checkbox'
                className='ml-3 mr-4'
                onChange={() => toggleCompany(initiative._id)}
                disabled={initiatives.length === 0}
                defaultChecked={requestForm.initiativeIds.includes(initiative._id)}
              />
              {initiative.name}
            </div>
          ))}
        </div>

        <div className='mt-3'>
          <p>If it is your company, you can request access and a message will be sent to the account owner.</p>
        </div>

        <Form>
          <div className='row'>
            <div className='col'>
              <FormGroup>
                <Label for='message'>Message to account owner / reason you want to join</Label>
                <Input
                  name='message'
                  placeholder='Reason for request to join'
                  type='textarea'
                  maxLength={240}
                  rows={4}
                  defaultValue={requestForm.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                />
                <div className='d-flex'>
                  <FormText className='text-left'>Optional</FormText>
                  <FormText className='ms-auto text-right'>{(requestForm.message ?? '').length}/240 chars</FormText>
                </div>
              </FormGroup>
            </div>
          </div>
        </Form>
        <div className='mt-3 d-flex'>
          <Button className='ml-auto' color='primary' disabled={!isFormValid} onClick={handleSendRequest}>
            Request to join company account
            <i className='ml-2 fa fal fa-paper-plane' />
          </Button>
        </div>
        <div className='mt-2 d-flex'>
          <Button className='ml-auto' color='primary' outline onClick={handleNext}>
            I want to set up a new company account
            <i className='ml-2 fa fal fa-angle-right' />
          </Button>
        </div>
      </section>
    </div>
  );
};
